import { initializeApp } from 'firebase/app'
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check'
import { getAuth, onAuthStateChanged, connectAuthEmulator } from 'firebase/auth'
import { initializeFirestore, connectFirestoreEmulator } from 'firebase/firestore'
import { getStorage, connectStorageEmulator } from 'firebase/storage'
import { getDatabase, connectDatabaseEmulator } from 'firebase/database'
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions'
import { decideAppCheck } from './appCheck'

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
}

// ¿Este arranque habla con los emuladores? `import.meta.env.DEV` es verdadero sólo
// bajo el servidor de desarrollo (`vite --mode dev`) y falso en TODO `vite build`,
// incluido `--mode dev`. Es UNA sola constante a propósito: gobierna a la vez la
// conexión a los emuladores (al final de este archivo) y la decisión de App Check,
// para que las dos no puedan divergir (ADR-SEC-023).
const usesEmulators = import.meta.env.DEV

export const firebaseApp = initializeApp(firebaseConfig)

// App Check (ADR-SEC-023): el cliente atesta con reCAPTCHA Enterprise ANTES de que
// exista cualquier servicio, para que ninguna petición a Firestore, RTDB, Storage,
// Auth o Functions pueda salir sin token. El orden lo fija
// tests/unit/services/firebase/config.appCheck.test.ts. Con los emuladores no se
// inicializa (no exigen App Check y `localhost` no está en ninguna llave); fuera de
// ellos, sin clave, `decideAppCheck` LANZA y la app no monta — nunca se inicializa
// con una clave vacía en silencio. App Check no autoriza nada: las reglas siguen
// siendo la frontera (ADR-SEC-014); el enforcement por servicio lo enciende el
// ecosistema cuando el tráfico sea «Verified».
const appCheckDecision = decideAppCheck({
  usesEmulators,
  siteKey: import.meta.env.VITE_APPCHECK_RECAPTCHA_SITE_KEY,
})
export const appCheck =
  appCheckDecision.kind === 'init'
    ? initializeAppCheck(firebaseApp, {
        provider: new ReCaptchaEnterpriseProvider(appCheckDecision.siteKey),
        // No es el default del SDK: sin esto el token (TTL 1 h) no se renueva en
        // segundo plano y el monitor del aula se quedaría sin atestación a la hora.
        isTokenAutoRefreshEnabled: true,
      })
    : null

export const auth = getAuth(firebaseApp)
// Firestore se inicializa forzando long polling (ADR-006, supersede ADR-003):
// en navegadores WebKit (Safari y todo navegador en iOS/iPadOS) el transporte
// WebChannel por defecto se cuelga de forma intermitente detrás de ciertas
// redes/proxies. La auto-detección (ADR-003) reaccionaba tarde y el fallo
// reapareció en campo (iPad/dev, 2026-06-21), así que se fuerza long polling
// (HTTP) desde el arranque: nunca se intenta WebChannel. Trade-off aceptado:
// algo más de overhead HTTP también en escritorio, despreciable para la carga
// del admin frente a la fiabilidad en iPad.
export const db = initializeFirestore(firebaseApp, {
  experimentalForceLongPolling: true,
})
export const storage = getStorage(firebaseApp)
export const realtimeDb = getDatabase(firebaseApp)

// Cloud Functions configuradas para la región us-central1
export const functions = getFunctions(firebaseApp, 'us-central1')

/**
 * Promise that resolves once Firebase Auth has restored the session from IndexedDB.
 * Router guards must await this before checking auth state on initial load.
 */
export const authReady: Promise<void> = new Promise((resolve) => {
  const unsubscribe = onAuthStateChanged(auth, () => {
    unsubscribe()
    resolve()
  })
})

// Conectar a Firebase Emulators en modo desarrollo (sincrónico para evitar race conditions).
// La condición es la misma constante que decidió no inicializar App Check (arriba).
if (usesEmulators) {
  try {
    connectFirestoreEmulator(db, 'localhost', 8080)
  } catch { /* ya conectado */ }

  try {
    connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true })
  } catch { /* ya conectado */ }

  try {
    connectStorageEmulator(storage, 'localhost', 9199)
  } catch { /* ya conectado */ }

  try {
    connectDatabaseEmulator(realtimeDb, 'localhost', 9000)
  } catch { /* ya conectado */ }

  try {
    connectFunctionsEmulator(functions, 'localhost', 5001)
  } catch { /* ya conectado */ }
}
